import {
  Bot,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  FileKey,
  FileText,
  Gauge,
  GitBranch,
  Github,
  Home,
  Layers,
  Lightbulb,
  LogOut,
  Menu,
  MessageSquare,
  Package,
  PieChart,
  Receipt,
  Rocket,
  ScrollText,
  Shield,
  Users,
  X,
  ShieldAlert,
} from "lucide-react";
import React, { ReactNode, useState } from "react";
import { useQuery } from "react-query";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { apiService } from "../services/api";
import { canManageUsers, isAdmin } from "../types";
import { appName, isProd, isProdAtlasProduct } from "../utils/env";
import { ThemeToggle } from "./ThemeToggle";

// The feature request board lives in DevXHub, which is built from this same
// codebase but deployed as a separate product. ProdAtlas links out to it rather
// than duplicating the feature. Absolute (not relative) because the two products
// are served from different hosts.
const DEVXHUB_FEATURE_REQUESTS_URL =
  "https://devxhub.scp.sandvik.com/feature-requests";

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isProdAtlas = isProdAtlasProduct();
  // Feature flag: Security Advisories is not yet released to production hosts.
  const securityAdvisoriesEnabled = !isProd();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [githubOpen, setGithubOpen] = useState(
    location.pathname.startsWith("/github"),
  );
  const [billingOpen, setBillingOpen] = useState(
    location.pathname.startsWith("/billing"),
  );

  const { data: copilotAccess } = useQuery(
    "copilot-access",
    () => apiService.checkCopilotAccess(),
    {
      enabled: !isProdAtlas,
      refetchOnWindowFocus: false,
      retry: false,
      select: (res) => res?.data?.data?.hasAccess ?? false,
    },
  );
  const hasCopilotAccess = !isProdAtlas && copilotAccess === true;

  const navigation = [
    ...(isProdAtlas
      ? [
          { name: "Dashboard", href: "/dashboard", icon: Home },
          { name: "Products", href: "/products", icon: Package },
          ...(securityAdvisoriesEnabled
            ? [
                {
                  name: "Security Advisories",
                  href: "/security-advisories",
                  icon: ScrollText,
                },
              ]
            : []),
          { name: "Applications", href: "/applications", icon: Layers },
          {
            name: "Threat Modelling",
            href: "/threat-modelling",
            icon: ShieldAlert,
          },
          { name: "Repositories", href: "/repositories", icon: GitBranch },
          ...(user?.roleId === "global_admin"
            ? [{ name: "DT API Keys", href: "/dt-api-keys", icon: Shield }]
            : []),
        ]
      : []),

    ...(!isProdAtlas
      ? [
          { name: "License", href: "/license", icon: FileKey },
          {
            name: "Feature Requests",
            href: "/feature-requests",
            icon: Lightbulb,
          },
          { name: "Onboarding", href: "/onboarding", icon: Rocket },
          { name: "Roadmaps", href: "/roadmap", icon: GitBranch },
        ]
      : []),
    ...(user?.isApprover === true
      ? [{ name: "Approvals", href: "/approvals", icon: CheckSquare }]
      : []),
    // User management for admins (global_admin, ba_admin, division_admin)
    ...(isProdAtlas && canManageUsers(user)
      ? [{ name: "Users", href: "/users", icon: Users }]
      : []),
  ];

  const githubNavigation = [
    { name: "Copilot", href: "/github/copilot", icon: Bot },
  ];

  const billingNavigation = [
    { name: "Summary", href: "/billing/summary", icon: PieChart },
    { name: "Invoices", href: "/billing/invoices", icon: FileText },
    { name: "Usage Caps", href: "/billing/usage-caps", icon: Gauge },
    {
      name: "License Requests",
      href: "/billing/license-requests",
      icon: ScrollText,
    },
  ];

  const isActive = (href: string) =>
    location.pathname === href || location.pathname.startsWith(href + "/");

  const renderCollapsibleSection = (
    label: string,
    icon: React.ElementType,
    items: typeof githubNavigation,
    isOpen: boolean,
    setOpen: (v: boolean) => void,
    closeSidebarOnNavigate: boolean,
  ) => (
    <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-700">
      <button
        type="button"
        onClick={() => setOpen(!isOpen)}
        className="group flex w-full items-center justify-between rounded-md px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
      >
        <span className="flex items-center">
          {React.createElement(icon, { className: "mr-3 h-5 w-5" })}
          {label}
        </span>
        {githubOpen ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </button>
      {isOpen && (
        <div className="mt-1 space-y-1 pl-3">
          {items.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`group flex items-center rounded-md px-2 py-2 text-sm font-medium ${
                isActive(item.href)
                  ? "bg-primary-100 dark:bg-primary-900 text-primary-900 dark:text-primary-100"
                  : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
              }`}
              onClick={
                closeSidebarOnNavigate ? () => setSidebarOpen(false) : undefined
              }
            >
              <item.icon className="mr-3 h-4 w-4" />
              {item.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );

  const renderGithubSection = (closeSidebarOnNavigate: boolean) =>
    renderCollapsibleSection(
      "GitHub",
      Github,
      githubNavigation,
      githubOpen,
      setGithubOpen,
      closeSidebarOnNavigate,
    );

  const renderBillingSection = (closeSidebarOnNavigate: boolean) =>
    renderCollapsibleSection(
      "Billing",
      Receipt,
      billingNavigation,
      billingOpen,
      setBillingOpen,
      closeSidebarOnNavigate,
    );

  // Only shown on ProdAtlas: DevXHub already reaches the same board through its
  // own "Feature Requests" sidebar entry, so there it would just link to itself.
  // Rendered in both the mobile and desktop sidebar footers.
  const renderFeedbackLink = () =>
    isProdAtlas ? (
      <a
        href={DEVXHUB_FEATURE_REQUESTS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="flex w-full items-center px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-md mb-3 border border-primary-200 dark:border-primary-800"
        title="Suggest an improvement or share feedback in DevXHub"
      >
        <Lightbulb className="mr-2 h-4 w-4" />
        <span className="flex-1">Feedback</span>
        <span className="text-xs text-primary-500 dark:text-primary-500">
          DevXHub
        </span>
      </a>
    ) : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-12">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? "block" : "hidden"}`}
        style={{ top: "3rem" }}
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white dark:bg-gray-900">
          <div className="flex h-16 items-center justify-between px-4">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {appName()}
            </h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isActive(item.href)
                    ? "bg-primary-100 dark:bg-primary-900 text-primary-900 dark:text-primary-100"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            ))}
            {hasCopilotAccess && renderGithubSection(true)}
            {!isProdAtlas && renderBillingSection(true)}
          </nav>
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <a
              href="https://teams.microsoft.com/l/channel/19%3AMb81OGCp1Yotr2FlzHxFv6qSh6uvntf5KbTcYOWbb9g1%40thread.tacv2/General?groupId=fff5bd60-10ea-4534-81c9-d0c9f95848ac&tenantId=e11cbe9c-f680-44b9-9d42-d705f740b888"
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-md mb-3 border border-primary-200 dark:border-primary-800"
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              <span className="flex-1">Support</span>
              <span className="text-xs text-primary-500 dark:text-primary-500">
                Teams
              </span>
            </a>
            {renderFeedbackLink()}
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-100">
                  {user?.name}
                  {user?.userType === "Guest" && (
                    <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                      (Guest)
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.email}
                </p>
                {user?.businessArea && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {user.businessArea}
                  </p>
                )}
                {user?.division && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {user.division}
                  </p>
                )}
                {user?.role && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {user.role}
                  </p>
                )}
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {isAdmin(user) ? "Admin" : "User"}
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="mt-3 flex w-full items-center px-2 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 rounded-md"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div
        className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col"
        style={{ top: "3rem" }}
      >
        <div className="flex flex-col flex-grow bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700">
          <div className="flex h-16 items-center justify-between px-4">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {appName()}
            </h1>
            <ThemeToggle />
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isActive(item.href)
                    ? "bg-primary-100 dark:bg-primary-900 text-primary-900 dark:text-primary-100"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
                }`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            ))}
            {hasCopilotAccess && renderGithubSection(false)}
            {!isProdAtlas && renderBillingSection(false)}
          </nav>
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            <a
              href="https://teams.microsoft.com/l/channel/19%3AMb81OGCp1Yotr2FlzHxFv6qSh6uvntf5KbTcYOWbb9g1%40thread.tacv2/General?groupId=fff5bd60-10ea-4534-81c9-d0c9f95848ac&tenantId=e11cbe9c-f680-44b9-9d42-d705f740b888"
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-md mb-3 border border-primary-200 dark:border-primary-800"
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              <span className="flex-1">Support</span>
              <span className="text-xs text-primary-500 dark:text-primary-500">
                Teams
              </span>
            </a>
            {renderFeedbackLink()}
            <div className="flex items-center">
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-100">
                  {user?.name}
                  {user?.userType === "Guest" && (
                    <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                      (Guest)
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.email}
                </p>
                {user?.businessArea && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {user.businessArea}
                  </p>
                )}
                {user?.division && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {user.division}
                  </p>
                )}
                {user?.role && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {user.role}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={logout}
              className="mt-3 flex w-full items-center px-2 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 rounded-md"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <div
          className="sticky z-40 flex h-16 items-center gap-x-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 shadow-sm lg:hidden"
          style={{ top: "3rem" }}
        >
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <Menu className="h-6 w-6" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {appName()}
          </h1>
          <div className="ml-auto">
            <ThemeToggle />
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto w-full px-4 sm:px-6 lg:px-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
